/**
 * 🗓 분기 재고 실사 · 📷 실사 사진 API 클라이언트 (v2.224.0 · 2026-10-01) — backend /stock/audit/* · /stock/photos/* · /expense/stock-audit/* 와 1:1.
 *
 * ⚠**서버 타입을 import 하지 않는다**(프론트는 늘 그랬다). 서버에서 값 이름을 바꾸면 이 파일을 함께 볼 것.
 * ⭐잠김(lock)·진행률(progress)·상태(state)는 **서버가 판정**한다 — 화면은 계산하지 않고 그대로 그린다.
 * ⭐사진 바이트는 NAS 에 있다 — `photoFileUrl(id)` 가 서버를 거쳐 받아 온다(브라우저가 하루 캐시).
 */

import type { ApiResponse } from './types';
import type { LocQty, StockLocation } from './stock-locations';

const BASE = '/api';

export class AuditApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function send<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init });
  let json: ApiResponse<T> | null = null;
  try {
    json = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new AuditApiError(res.status, 'BAD_RESPONSE', `서버 응답을 읽지 못했습니다 (${res.status})`);
  }
  if (!json.ok) {
    // ⚠우리 모양이 아닌 오류(서버 앞단 404 「Route … not found」 등)는 error 가 객체가 아니다 — 그대로 읽으면
    //   글자 없는 노란 띠가 뜬다(v2.225.1 · 10-02 EDS 16TB). 상태 번호라도 보이게 한다.
    const raw = json as unknown as { error?: unknown; message?: unknown };
    const e = raw.error;
    const code = e && typeof e === 'object' ? String((e as { code?: unknown }).code ?? 'ERROR') : 'ERROR';
    const em = e && typeof e === 'object' ? (e as { message?: unknown }).message : undefined;
    const msg = typeof em === 'string' && em
      ? em
      : `서버가 요청을 처리하지 못했습니다 (${res.status}${typeof raw.message === 'string' ? ` · ${raw.message}` : ''})`;
    throw new AuditApiError(res.status, code, msg);
  }
  return json.data;
}

function postJson<T>(path: string, body?: unknown, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
  return send<T>(path, {
    method,
    ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  });
}

/* ── 분기 실사 ───────────────────────────────────── */

export type AuditItemStatus = 'pending' | 'later' | 'ok' | 'diff' | 'skip';
export type AuditState = 'counting' | 'counted' | 'reviewing' | 'closed';

export interface AuditProgress {
  total: number;
  decided: number;
  ok: number;
  diff: number;
  skip: number;
  later: number;
  pending: number;
  allDecided: boolean;
}

export interface AuditLock {
  locked: boolean;
  closed: boolean;
  reason: string | null;
}

export interface AuditReportRef {
  docId: number;
  docNo: string;
  status: string;
  authorName: string;
  submittedAt: string | null;
  rejectReason: string | null;
}

/** quarter 분기 실사 · adhoc 🔎 수시 실물확인 (v2.324.0 · 개발요청 #31 · 옛 서버는 안 준다 → quarter 로 읽는다) */
export type AuditKind = 'quarter' | 'adhoc';

export interface AuditSummary {
  id: number;
  kind?: AuditKind;
  year: number;
  /** 수시 실물확인은 null */
  quarter: number | null;
  /** 수시 실물확인 이름·날짜 — 분기 실사는 null */
  title?: string | null;
  checkDate?: string | null;
  /** 「2026년 3분기」 · 「설 연휴 전 확인 (2027-02-05)」 */
  label: string;
  /** 「2026년 3분기 실사」 · 「수시 실물확인 · 설 연휴 전 확인 (2027-02-05)」 (v2.324.0 · 옛 서버는 없음) */
  heading?: string;
  startedAt: string;
  startedByName: string;
  scopeCount: number;
  progress: AuditProgress;
  report: AuditReportRef | null;
  lock: AuditLock;
  /** 그 실사에 남은 사진 장수 — [실사 취소] 확인 창 (v2.225.0) */
  photoCount: number;
  state: AuditState;
  stateLabel: string;
}

export interface AuditList {
  year: number;
  today: string;
  current: { year: number; quarter: number; label: string };
  canEdit: boolean;
  canDelete: boolean;
  startBlockedBy: AuditSummary | null;
  counting: AuditSummary | null;
  latest: AuditSummary | null;
  owner: { userId: string; label: string } | null;
  audits: AuditSummary[];
}

export interface AuditItem {
  ord: number;
  itemCode: string;
  itemName: string | null;
  itemGroup: string | null;
  displayGroup: string | null;
  startQty: number;
  startPending: number;
  currentStock: number | null;
  pendingDelivery: number | null;
  status: AuditItemStatus;
  systemQty: number | null;
  countedQty: number | null;
  note: string | null;
  decidedBy: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  photoCount: number;
  /** 장소별 센 수(v2.226.0) — 「나중에」 일부 입력도 여기 · 옛 기록·옛 서버는 null/없음 */
  locQty?: LocQty | null;
}

export interface AuditDetail {
  audit: {
    id: number; kind?: AuditKind; year: number; quarter: number | null; title?: string | null; checkDate?: string | null;
    label: string; heading?: string; startedAt: string; startedByName: string;
    scopeCount: number; state: AuditState; stateLabel: string;
  };
  today: string;
  progress: AuditProgress;
  lock: AuditLock;
  canEdit: boolean;
  canDelete: boolean;
  canSubmitReport: boolean;
  submitHint: string | null;
  owner: { userId: string; label: string } | null;
  report: AuditReportRef | null;
  /** 장소 칸(v2.226.0) — 옛 서버는 안 준다(없으면 장소 칸을 안 그린다) */
  locations?: StockLocation[];
  skipChips: string[];
  diffChips: string[];
  items: AuditItem[];
}

export function listAudits(year?: number): Promise<AuditList> {
  return send<AuditList>(`/stock/audit${year ? `?year=${year}` : ''}`);
}

export function startAudit(year: number, quarter: number): Promise<{ id: number; scopeCount: number }> {
  return postJson('/stock/audit', { year, quarter });
}

/** 🔎 수시 실물확인 시작 (v2.324.0) — itemCodes null = 잔여재고 전체 · 배열 = 고른 품목 */
export function startAdhocAudit(input: { title: string; checkDate: string; itemCodes: string[] | null }): Promise<{ id: number; scopeCount: number }> {
  return postJson('/stock/audit', { kind: 'adhoc', ...input });
}

/** 수시 실물확인인가 — 옛 서버(kind 없음)는 분기로 본다 */
export function isAdhoc(a: { kind?: AuditKind; quarter?: number | null }): boolean {
  return a.kind === 'adhoc' || a.quarter === null;
}

/** 제목 줄 — 서버가 준 heading, 없으면(옛 서버) 「라벨 실사」 */
export function headingOf(a: { heading?: string; label: string }): string {
  return a.heading ?? `${a.label} 실사`;
}

export function getAudit(id: number): Promise<AuditDetail> {
  return send<AuditDetail>(`/stock/audit/${id}`);
}

export interface DecideInput {
  status: 'ok' | 'diff' | 'skip' | 'later';
  countedQty?: number | null;
  note?: string | null;
  /** 누를 때 화면에 떠 있던 현재고 — 저장 순간과 다르면 서버가 moved 로 알려 준다 */
  seenQty?: number | null;
  /** 장소별 센 수(v2.226.0) — 보내면 서버가 합계로 맞음·다름을 판정 · 나중에는 센 장소만 남긴다 */
  locQty?: LocQty;
}

export interface DecideResult {
  itemCode: string;
  /** 실제로 저장된 상태(장소별 수량이면 서버 판정) */
  status: string;
  countedQty?: number | null;
  moved: { seen: number; now: number } | null;
  progress: AuditProgress;
}

/**
 * ⭐품목코드는 **본문**에 싣는다(v2.225.1). 주소 경로에 넣으면 운영 nginx 가 `%2F` 를 `/` 로 풀어
 *   빗금 든 코드(EDS 16TB `PFRUKTXDXE022-14/42` · Seagate 20TB)가 404 였다 — 개발 PC(vite)에서는 재현 안 됨.
 */
export function decideItem(auditId: number, itemCode: string, input: DecideInput): Promise<DecideResult> {
  return postJson(`/stock/audit/${auditId}/decide`, { ...input, itemCode });
}

export function undoItem(auditId: number, itemCode: string): Promise<{ itemCode: string; progress: AuditProgress }> {
  return postJson(`/stock/audit/${auditId}/undo`, { itemCode });
}

export interface CancelAuditResult {
  id: number;
  label: string;
  decided: number;
  /** NAS 파일까지 지운 사진 */
  photosDeleted: number;
  /** 기록만 있고 NAS 에는 이미 없던 사진 */
  photosMissing: number;
}

/** [실사 취소] — 기록·사진(NAS 파일까지) 모두 지운다 · mgmt 이상 · 보고서가 붙었으면 400 (v2.225.0) */
export function cancelAudit(auditId: number): Promise<CancelAuditResult> {
  return send(`/stock/audit/${auditId}`, { method: 'DELETE' });
}

/* ── 보고서 ──────────────────────────────────────── */

export interface AuditReportCheck {
  canSubmit: boolean;
  reason: string | null;
  existing: { docId: number; docNo: string; status: string } | null;
}

export function checkAuditReport(auditId: number): Promise<AuditReportCheck> {
  return send(`/expense/stock-audit/check?auditId=${auditId}`);
}

export function createAuditReport(input: { auditId: number; notes: string; submit: boolean }):
  Promise<{ docId: number; docNo: string; status: string; auditId: number }> {
  return postJson('/expense/stock-audit', input);
}

export function updateAuditReport(docId: number, input: { notes: string; submit: boolean }):
  Promise<{ docId: number; docNo: string; status: string }> {
  return postJson(`/expense/stock-audit/${docId}`, input, 'PUT');
}

/**
 * 상세(GET /expense/docs/:id)의 stockAudit 칸 — 다른 종류면 null.
 * ⚠품목 전체는 여기 없다(상신 때 굳은 스냅샷은 확정본 PDF 에만). 실제와 다른 품목만 요약으로 온다.
 */
export interface StockAuditReportBody {
  auditId: number;
  year: number;
  quarter: number;
  label: string;
  period: { from: string; to: string };
  notes: string;
  counters: AuditProgress;
  qty: { startTotal: number; systemTotal: number; countedTotal: number };
  photoTotal: number;
  startedAt: string;
  startedByName: string;
  finishedAt: string | null;
  checkers: string[];
  diffs: {
    itemCode: string; itemName: string | null; systemQty: number | null; countedQty: number | null;
    delta: number | null; note: string | null; photoCount: number;
  }[];
  createdAt: string | null;
  updatedAt: string | null;
}

/* ── 사진 ────────────────────────────────────────── */

export type PhotoKey =
  | { scope: 'weekly'; weekDate?: string; round?: number; itemCode?: string | null }
  | { scope: 'audit'; auditId: number; itemCode?: string | null };

export interface StockPhoto {
  id: number;
  scope: 'weekly' | 'audit';
  weekDate: string | null;
  round: number | null;
  auditId: number | null;
  itemCode: string;
  filename: string;
  nasPath: string;
  bytes: number;
  mime: string;
  width: number | null;
  height: number | null;
  takenBy: string;
  takenByName: string;
  takenAt: string;
  file: string;
}

export interface PhotoList {
  storeKind: 'nas' | 'local';
  photos: StockPhoto[];
  countsByItem: Record<string, number>;
}

export function photoQuery(key: PhotoKey): string {
  const sp = new URLSearchParams({ scope: key.scope });
  if (key.scope === 'weekly') {
    if (key.weekDate) sp.set('week', key.weekDate);
    if (key.round) sp.set('round', String(key.round));
  } else {
    sp.set('auditId', String(key.auditId));
  }
  if (key.itemCode) sp.set('itemCode', key.itemCode);
  return sp.toString();
}

export function listPhotos(key: PhotoKey): Promise<PhotoList> {
  return send<PhotoList>(`/stock/photos?${photoQuery(key)}`);
}

/** 그림 주소 — 서버가 NAS 에서 받아 돌려준다. 같은 번호는 늘 같은 파일(브라우저가 하루 캐시) */
export function photoFileUrl(id: number): string {
  return `${BASE}/stock/photos/${id}/file`;
}

export async function uploadPhoto(
  key: { scope: 'weekly' } | { scope: 'audit'; auditId: number },
  itemCode: string,
  blob: Blob,
  filename = 'photo.jpg',
): Promise<StockPhoto> {
  const fd = new FormData();
  fd.append('scope', key.scope);
  if (key.scope === 'audit') fd.append('auditId', String(key.auditId));
  fd.append('itemCode', itemCode);
  fd.append('file', blob, filename);
  const r = await send<{ photo: StockPhoto; storeKind: string }>('/stock/photos', { method: 'POST', body: fd });
  return r.photo;
}

export function deletePhoto(id: number): Promise<{ id: number; existed: boolean }> {
  return send(`/stock/photos/${id}`, { method: 'DELETE' });
}
